import { createContext, type Dispatch, useContext } from "react";

import { ContrastModes, SettingsActions } from "@/types/settings";

export type SettingsActionPayload =
	| {
			contrastMode: ContrastModes;
			type: SettingsActions.UpdateContrastMode;
	  }
	| {
			isSettingsModalOpen: boolean;
			type: SettingsActions.UpdateSettingsModalVisibility;
	  };

/**
 * Initial state for settings reducer.
 * Note: This only contains the state values, not the helper functions.
 */
export type SettingsState = {
	contrastMode: ContrastModes;
	isSettingsModalOpen: boolean;
};

export const initialSettingsState: SettingsState = {
	contrastMode: ContrastModes.SYSTEM,
	isSettingsModalOpen: false,
};

/**
 * Reducer function for managing settings state.
 * This follows the reducer pattern for predictable state updates.
 */
export const settingsReducer = (
	state: SettingsState,
	action: SettingsActionPayload,
): SettingsState => {
	switch (action.type) {
		case SettingsActions.UpdateContrastMode:
			return {
				...state,
				contrastMode: action.contrastMode,
			};
		case SettingsActions.UpdateSettingsModalVisibility:
			return {
				...state,
				isSettingsModalOpen: action.isSettingsModalOpen,
			};
		default:
			return state;
	}
};

/**
 * Action creators - helper functions to create action payloads.
 * These provide a clean API for dispatching actions.
 */
export const settingsActions = {
	updateContrastMode: (contrastMode: ContrastModes): SettingsActionPayload => ({
		contrastMode,
		type: SettingsActions.UpdateContrastMode,
	}),
	updateSettingsModalVisibility: (
		isSettingsModalOpen: boolean,
	): SettingsActionPayload => ({
		isSettingsModalOpen,
		type: SettingsActions.UpdateSettingsModalVisibility,
	}),
};

/**
 * Context for accessing settings state.
 * Use useSettingsState() hook to access this context.
 * Initialized with undefined to enable proper error checking.
 */
export const SettingsStateContext = createContext<SettingsState | undefined>(
	undefined,
);

/**
 * Context for dispatching settings actions.
 * Use useSettingsDispatch() hook to access this context.
 * Initialized with undefined to enable proper error checking.
 */
export const SettingsDispatchContext = createContext<
	Dispatch<SettingsActionPayload> | undefined
>(undefined);

/**
 * Hook to access settings state from context.
 * @throws Error if used outside of SettingsProvider
 */
export const useSettingsState = (): SettingsState => {
	const context = useContext(SettingsStateContext);
	if (context === undefined) {
		throw new Error("useSettingsState must be used within a SettingsProvider");
	}
	return context;
};

/**
 * Hook to access settings dispatch function from context.
 * @throws Error if used outside of SettingsProvider
 */
export const useSettingsDispatch = (): Dispatch<SettingsActionPayload> => {
	const context = useContext(SettingsDispatchContext);
	if (context === undefined) {
		throw new Error(
			"useSettingsDispatch must be used within a SettingsProvider",
		);
	}
	return context;
};
